import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Topbar from "../components/Topbar";
import useLang from "../hooks/useLang";
import { useAuth } from "../context/AuthContext";
import api from "../api";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Users, Brain, ChatCircle, Sparkle, Calendar, Eye } from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";

function StatCard({ label, value, sub }) {
  return (
    <div className="border border-zinc-200 rounded-md p-6 bg-white">
      <div className="overline mb-2">/// {label}</div>
      <div className="font-heading text-4xl tracking-tight font-light">{value ?? 0}</div>
      {sub != null && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}

function ActivityBar({ data }) {
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <div className="border border-zinc-200 rounded-md p-6">
      <div className="overline mb-4">/// Conversazioni · ultimi 14 giorni</div>
      <div className="flex items-end gap-1.5 h-32">
        {data.length === 0 ? (
          <p className="text-sm text-zinc-500">Nessuna attività</p>
        ) : (
          data.map((d) => (
            <div key={d.date} className="flex-1 flex flex-col items-center gap-1.5">
              <div title={`${d.date}: ${d.count}`} className="w-full bg-zinc-900 rounded-sm transition-all" style={{ height: `${(d.count / max) * 100}%` }} />
              <div className="text-[9px] font-mono text-zinc-400">{d.date.slice(5)}</div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { t } = useLang();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [clones, setClones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendingWeekly, setSendingWeekly] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user || !user.is_admin) {
      navigate("/");
      return;
    }
    (async () => {
      try {
        const [s, u, c] = await Promise.all([
          api.get("/admin/stats"),
          api.get("/admin/users"),
          api.get("/admin/clones"),
        ]);
        setStats(s.data);
        setUsers(u.data);
        setClones(c.data);
      } catch (err) {
        toast.error(err.response?.data?.detail || t("errorGeneric"));
      } finally {
        setLoading(false);
      }
    })();
  // eslint-disable-next-line
  }, [user, authLoading]);

  const sendDailyNow = async () => {
    setSending(true);
    try {
      await api.post("/admin/send-daily-now");
      toast.success(t("dailyEmailSent"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("errorGeneric"));
    } finally {
      setSending(false);
    }
  };

  const sendWeeklyNow = async () => {
    setSendingWeekly(true);
    try {
      await api.post("/admin/send-weekly-now");
      toast.success("Weekly digest inviato");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("errorGeneric"));
    } finally {
      setSendingWeekly(false);
    }
  };

  const showWeeklyPreview = async () => {
    try {
      const { data } = await api.get("/admin/weekly-preview");
      if (!data.has_content) {
        toast.info("Nessuna attività settimanale da riepilogare");
        return;
      }
      setPreviewHtml(data.html);
      setShowPreview(true);
    } catch (err) {
      toast.error(t("errorGeneric"));
    }
  };

  if (loading || !user?.is_admin) {
    return (
      <div className="min-h-screen bg-white">
        <Topbar />
        <div className="p-12 text-zinc-500 font-mono text-sm">{t("loading")}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Topbar />
      <main className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 py-10">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <div className="overline mb-3">/// {t("admin")}</div>
            <h1 className="font-heading text-4xl sm:text-5xl tracking-tight font-light">{t("adminTitle")}</h1>
            <p className="text-zinc-500 mt-2">{t("adminSubtitle")}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="secondary" onClick={showWeeklyPreview} data-testid="admin-preview-weekly-btn" className="rounded-md gap-2 border border-zinc-200 bg-white hover:bg-zinc-50">
              <Eye size={16} /> Preview weekly
            </Button>
            <Button onClick={sendWeeklyNow} disabled={sendingWeekly} data-testid="admin-send-weekly-btn" className="rounded-md gap-2 border border-zinc-200 bg-white text-zinc-900 hover:bg-zinc-50">
              <Calendar size={16} /> {sendingWeekly ? t("loading") : "Invia digest settimanale"}
            </Button>
            <Button onClick={sendDailyNow} disabled={sending} data-testid="admin-send-daily-btn" className="bg-zinc-950 hover:bg-zinc-800 rounded-md gap-2">
              <Sparkle size={16} weight="fill" /> {sending ? t("loading") : t("sendDailyNow")}
            </Button>
          </div>
        </div>

        <Dialog open={showPreview} onOpenChange={setShowPreview}>
          <DialogContent className="max-w-3xl rounded-md border-zinc-200 max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl tracking-tight">Weekly digest preview</DialogTitle>
            </DialogHeader>
            <div className="border border-zinc-200 rounded-md overflow-hidden bg-zinc-50" data-testid="weekly-preview-container">
              <iframe
                title="Weekly digest preview"
                srcDoc={previewHtml || ""}
                className="w-full min-h-[500px] bg-white"
              />
            </div>
          </DialogContent>
        </Dialog>

        <Tabs defaultValue="overview">
          <TabsList className="bg-transparent border-b border-zinc-200 rounded-none w-full justify-start gap-1 p-0 h-auto mb-8">
            <TabsTrigger value="overview" data-testid="admin-tab-overview" className="rounded-none border-b-2 border-transparent data-[state=active]:border-zinc-950 data-[state=active]:bg-transparent px-4 py-3 text-sm font-mono uppercase tracking-wider data-[state=active]:shadow-none">
              Overview
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users" className="rounded-none border-b-2 border-transparent data-[state=active]:border-zinc-950 data-[state=active]:bg-transparent px-4 py-3 text-sm font-mono uppercase tracking-wider data-[state=active]:shadow-none">
              {t("usersTab")}
            </TabsTrigger>
            <TabsTrigger value="clones" data-testid="admin-tab-clones" className="rounded-none border-b-2 border-transparent data-[state=active]:border-zinc-950 data-[state=active]:bg-transparent px-4 py-3 text-sm font-mono uppercase tracking-wider data-[state=active]:shadow-none">
              {t("clonesTab")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-0 space-y-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard label={t("totalUsers")} value={stats?.users?.total} sub={`${stats?.users?.active_7d} ${t("activeUsers7").toLowerCase()}`} />
              <StatCard label={t("totalClones")} value={stats?.clones?.total} sub={`${stats?.clones?.public} ${t("publicClones").toLowerCase()}`} />
              <StatCard label={t("totalConversations")} value={stats?.conversations?.total} sub={`${stats?.conversations?.last_7d} ${t("conversations7").toLowerCase()}`} />
              <StatCard label={t("diaryEntries")} value={stats?.answers?.diary} sub={`${stats?.answers?.total} risposte totali`} />
            </div>
            <ActivityBar data={stats?.daily_activity || []} />
            <div className="border border-zinc-200 rounded-md p-6">
              <div className="overline mb-4">/// {t("topClones")}</div>
              {stats?.top_clones?.length ? (
                <div className="divide-y divide-zinc-100">
                  {stats.top_clones.map((c) => (
                    <div key={c.id} className="flex items-center justify-between py-3">
                      <div>
                        <p className="font-medium">{c.role_name}</p>
                        <p className="text-xs text-zinc-500 font-mono uppercase tracking-wider">{c.industry} · {c.owner_name}</p>
                      </div>
                      <span className="tag">{c.conversations} conv</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-zinc-500">Nessuna conversazione ancora</p>
              )}
            </div>
          </TabsContent>

          <TabsContent value="users" className="mt-0">
            <div className="border border-zinc-200 rounded-md overflow-hidden" data-testid="admin-users-table">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b border-zinc-200">
                  <tr>
                    <th className="text-left p-3 overline font-normal">Name</th>
                    <th className="text-left p-3 overline font-normal">Email</th>
                    <th className="text-left p-3 overline font-normal">Cloni</th>
                    <th className="text-left p-3 overline font-normal">Creato</th>
                    <th className="text-left p-3 overline font-normal">Ultimo login</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-zinc-100 hover:bg-zinc-50/50">
                      <td className="p-3">{u.name} {u.is_admin && <span className="tag tag-accent ml-2">ADMIN</span>}</td>
                      <td className="p-3 font-mono text-xs">{u.email}</td>
                      <td className="p-3">{u.clones_count}</td>
                      <td className="p-3 font-mono text-xs text-zinc-500">{(u.created_at || "").slice(0, 10)}</td>
                      <td className="p-3 font-mono text-xs text-zinc-500">{(u.last_login_at || "").slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="clones" className="mt-0">
            <div className="border border-zinc-200 rounded-md overflow-hidden" data-testid="admin-clones-table">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b border-zinc-200">
                  <tr>
                    <th className="text-left p-3 overline font-normal">Role</th>
                    <th className="text-left p-3 overline font-normal">Industry</th>
                    <th className="text-left p-3 overline font-normal">Owner</th>
                    <th className="text-left p-3 overline font-normal">Visibility</th>
                    <th className="text-left p-3 overline font-normal">Answers</th>
                    <th className="text-left p-3 overline font-normal">Convs</th>
                  </tr>
                </thead>
                <tbody>
                  {clones.map((c) => (
                    <tr key={c.id} className="border-b border-zinc-100 hover:bg-zinc-50/50">
                      <td className="p-3">{c.avatar_emoji} {c.role_name}</td>
                      <td className="p-3 text-zinc-600">{c.industry}</td>
                      <td className="p-3 font-mono text-xs">{c.owner_name}</td>
                      <td className="p-3"><span className="tag">{c.visibility}</span></td>
                      <td className="p-3">{c.answer_count}</td>
                      <td className="p-3">{c.conversation_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
